import React, { useEffect } from 'react'
import { Button } from '~/components/common/Button'
import { Typography } from '~/components/common/Typography'

export interface TriggerConfirmationModalProps {
  /** Whether the modal is open */
  isOpen: boolean
  /** Callback to close the modal */
  onClose: () => void
  /** Callback when user confirms to continue */
  onContinue: () => void
}

// Info icon (16px, gray) for notice section
const InfoIcon = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 14 14'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className='shrink-0'
    style={{ display: 'block' }}
  >
    <g>
      <path
        d='M7 0C3.13438 0 0 3.13438 0 7C0 10.8656 3.13438 14 7 14C10.8656 14 14 10.8656 14 7C14 3.13438 10.8656 0 7 0ZM7 12.8125C3.79063 12.8125 1.1875 10.2094 1.1875 7C1.1875 3.79063 3.79063 1.1875 7 1.1875C10.2094 1.1875 12.8125 3.79063 12.8125 7C12.8125 10.2094 10.2094 12.8125 7 12.8125Z'
        fill='black'
        fillOpacity='0.45'
      />
      <path
        d='M6.25 4.25C6.25 4.44891 6.32902 4.63968 6.46967 4.78033C6.61032 4.92098 6.80109 5 7 5C7.19891 5 7.38968 4.92098 7.53033 4.78033C7.67098 4.63968 7.75 4.44891 7.75 4.25C7.75 4.05109 7.67098 3.86032 7.53033 3.71967C7.38968 3.57902 7.19891 3.5 7 3.5C6.80109 3.5 6.61032 3.57902 6.46967 3.71967C6.32902 3.86032 6.25 4.05109 6.25 4.25ZM7.375 6H6.625C6.55625 6 6.5 6.05625 6.5 6.125V10.375C6.5 10.4438 6.55625 10.5 6.625 10.5H7.375C7.44375 10.5 7.5 10.4438 7.5 10.375V6.125C7.5 6.05625 7.44375 6 7.375 6Z'
        fill='black'
        fillOpacity='0.45'
      />
    </g>
  </svg>
)

export function TriggerConfirmationModal({
  isOpen,
  onClose,
  onContinue
}: TriggerConfirmationModalProps) {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div className='fixed inset-0 z-50 bg-black/45' onClick={handleOverlayClick}>
      {/* Modal positioned 120px from top */}
      <div
        className='absolute left-1/2 top-[120px] w-[638px] -translate-x-1/2 rounded-[28px] bg-white px-[24px] py-[32px] shadow-popover'
        onClick={e => e.stopPropagation()}
      >
        {/* Content wrapper */}
        <div className='flex flex-col gap-[48px]'>
          {/* Text content section */}
          <div className='flex flex-col gap-[32px]'>
            {/* Title */}
            <Typography
              fontFamily='geist'
              fontWeight='bold'
              className='text-[rgba(0,0,0,0.88)]'
              style={{ fontSize: '20px', fontWeight: 700, lineHeight: '22px' }}
            >
              Testing Triggered and Scheduled Flows?
            </Typography>

            {/* Body section */}
            <div className='flex flex-col gap-[24px]'>
              {/* Main body text */}
              <Typography
                fontFamily='geist'
                fontWeight='medium'
                className='text-[rgba(0,0,0,0.88)]'
                style={{ fontSize: '14px', fontWeight: 500, lineHeight: '22px' }}
              >
                Some of your selected flows are scheduled or trigger-based. We'll test these by
                creating temporary REST endpoints that call the flows with sample data. A new
                application will be deployed to CloudHub for this simulation.
                <br />
                <br />
                Would you like to proceed? If not, click Cancel and unselect the triggered or
                scheduled flows.
              </Typography>

              {/* Info notice */}
              <div className='flex items-center gap-[4px]'>
                <InfoIcon />
                <Typography
                  fontFamily='geist'
                  fontWeight='normal'
                  className='text-[rgba(0,0,0,0.65)]'
                  style={{ fontSize: '12px', fontWeight: 400, lineHeight: '14px' }}
                >
                  Additional deployments made will be deleted by the agent after testing.
                </Typography>
              </div>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className='flex justify-end gap-[5px]'>
            <Button
              variant='outlined'
              onClick={onClose}
              className='h-[36px] rounded-[12px] px-[16px] py-[8px]'
            >
              <Typography
                fontFamily='geist'
                fontWeight='medium'
                className='whitespace-nowrap'
                style={{ fontSize: '14px', fontWeight: 500, lineHeight: '22px' }}
              >
                Cancel
              </Typography>
            </Button>

            <Button
              variant='primary'
              onClick={onContinue}
              className='h-[36px] rounded-[12px] bg-[#262626] px-[24px] py-[8px] hover:bg-[#383838]'
            >
              <Typography
                fontFamily='geist'
                fontWeight='medium'
                className='whitespace-nowrap text-white'
                style={{ fontSize: '14px', fontWeight: 500, lineHeight: '22px' }}
              >
                Yes, proceed
              </Typography>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TriggerConfirmationModal
